import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../App';

const fmt = n => `${Number(n).toFixed(2)} €`;
const fmtDate = s => s ? s.split('-').reverse().join('/') : '—';

function Invoice({ password }) {
  const now = new Date();
  const [month, setMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [contactName, setContactName] = useState('Antoine Declef');
  const [contactEmail, setContactEmail] = useState('');
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await apiFetch(`/hostex/reservations?month=${month}`, password);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Erreur API Hostex');
      setReservations(data.reservations.filter(r => !r.warning));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [month, password]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createInvoice = async () => {
    if (!contactName.trim()) return alert('Renseigne le nom du client');
    setCreating(true);
    setError('');
    setResult(null);
    try {
      const res = await apiFetch('/zoho/invoice', password, {
        method: 'POST',
        body: JSON.stringify({ contact_name: contactName, contact_email: contactEmail, month, reservations })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Erreur création facture Zoho');
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const totalMenage = reservations.reduce((a, r) => a + (r.cleaning_fee ?? 0), 0);
  const totalCommission = reservations.reduce((a, r) => a + (r.ma_commission ?? 0), 0);
  const totalFacture = totalMenage + totalCommission;

  return (
    <div className="section">
      <div className="toolbar">
        <div className="toolbar-left">
          <label>Client :</label>
          <input
            type="text" value={contactName}
            onChange={e => setContactName(e.target.value)}
            className="input-sm" placeholder="Nom du client Zoho"
            style={{ width: 200 }}
          />
          <input
            type="email" value={contactEmail}
            onChange={e => setContactEmail(e.target.value)}
            className="input-sm" placeholder="Email (optionnel)"
            style={{ width: 200 }}
          />
        </div>
        <div className="toolbar-right">
          <label>Mois :</label>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="input-sm" />
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {result && (
        <div className="success-box">
          Facture créée ✓ — <strong>{result.invoice_number}</strong> — {fmt(result.total)}
          {' '}
          <a href={result.url} target="_blank" rel="noreferrer" className="invoice-link">
            Voir sur Zoho Invoice →
          </a>
        </div>
      )}

      {loading ? (
        <div className="loading">Chargement des réservations...</div>
      ) : (
        <>
          <h3 className="section-title">
            Lignes de facture — {month}
            {reservations.length > 0 && ` (${reservations.length} réservation${reservations.length > 1 ? 's' : ''})`}
          </h3>

          {reservations.length === 0 ? (
            <div className="empty">
              Aucune réservation avec données complètes pour cette période.
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style={{ textAlign: 'right' }}>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map(r => {
                    const dates = `${fmtDate(r.check_in)} → ${fmtDate(r.check_out)}`;
                    return (
                      <React.Fragment key={r.id}>
                        <tr>
                          <td>Frais de ménage — {r.property} — {dates}</td>
                          <td style={{ textAlign: 'right' }}>{fmt(r.cleaning_fee)}</td>
                        </tr>
                        <tr>
                          <td>Commission conciergerie 20% — {r.property} — {dates}</td>
                          <td style={{ textAlign: 'right' }}>{fmt(r.ma_commission)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td>Total Frais de ménage</td>
                    <td style={{ textAlign: 'right' }}>{fmt(totalMenage)}</td>
                  </tr>
                  <tr className="totals-row">
                    <td>Total Commission 20%</td>
                    <td style={{ textAlign: 'right' }}>{fmt(totalCommission)}</td>
                  </tr>
                  <tr className="totals-row grand-total">
                    <td><strong>TOTAL FACTURE</strong></td>
                    <td style={{ textAlign: 'right' }}><strong>{fmt(totalFacture)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {reservations.length > 0 && (
            <div className="invoice-actions">
              <button
                className="btn btn-primary"
                onClick={createInvoice}
                disabled={creating}
              >
                {creating ? 'Création en cours...' : 'Créer la facture sur Zoho Invoice'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Invoice;
